import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, eur, dateFr, downloadFile, apiError } from "@/lib/api";
import StatutBadge from "@/components/StatutBadge";
import Modal from "@/components/Modal";

export default function DevisFactures() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("devis");
  const [filtre, setFiltre] = useState("");
  const [devis, setDevis] = useState([]);
  const [factures, setFactures] = useState([]);
  const [corbeilleOpen, setCorbeilleOpen] = useState(false);
  const [corbeille, setCorbeille] = useState([]);
  const navigate = useNavigate();

  function ouvrirCorbeille() {
    api.get("/corbeille").then((r) => setCorbeille(r.data)).catch(() => {});
    setCorbeilleOpen(true);
  }

  async function restaurer(doc) {
    try {
      await api.post(`/${doc.type === "devis" ? "devis" : "factures"}/${doc.id}/restaurer`);
      toast.success(t("liste.restaure", { numero: doc.numero }));
      setCorbeille((c) => c.filter((x) => x.id !== doc.id));
      api.get("/devis").then((r) => setDevis(r.data));
      api.get("/factures").then((r) => setFactures(r.data));
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  const FILTRES_DEVIS = [["", t("liste.tous")], ["brouillon", t("liste.brouillons")], ["envoye", t("liste.envoyes")], ["accepte", t("liste.acceptes")], ["refuse", t("liste.refuses")]];
  const FILTRES_FACTURES = [["", t("liste.toutes")], ["a_envoyer", t("statuts.a_envoyer")], ["envoyee", t("liste.envoyees")], ["payee", t("liste.payees")], ["en_retard", t("liste.en_retard")]];

  useEffect(() => {
    api.get("/devis").then((r) => setDevis(r.data));
    api.get("/factures").then((r) => setFactures(r.data));
  }, []);

  const nbAEnvoyer = factures.filter((f) => f.statut === "a_envoyer").length;
  const rows = (tab === "devis" ? devis : factures).filter((d) => !filtre || d.statut === filtre);
  const filtres = tab === "devis" ? FILTRES_DEVIS : FILTRES_FACTURES;

  async function exportCsv() {
    try {
      await downloadFile("/factures/export/csv", "export_factures.csv");
      toast.success(t("liste.export_ok"));
    } catch (e) {
      toast.error(apiError(e, t("liste.export_erreur")));
    }
  }

  return (
    <div className="space-y-5" data-testid="page-devis-factures">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("liste.titre")}</h1>
        <div className="flex gap-2">
          {tab === "factures" && (
            <button onClick={exportCsv} className="btn-secondary" data-testid="btn-export-compta">
              <Download size={18} /> {t("liste.export_compta")}
            </button>
          )}
          <button onClick={ouvrirCorbeille} className="btn-secondary" data-testid="btn-corbeille">
            <Trash2 size={18} /> {t("liste.corbeille")}
          </button>
          <Link to="/devis/nouveau" className="btn-accent" data-testid="btn-nouveau-devis">
            <Plus size={20} /> {t("liste.nouveau_devis")}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-white rounded-lg p-1 border border-[#E6DFD1] max-w-sm">
        {[["devis", t("liste.tab_devis")], ["factures", t("liste.tab_factures")]].map(([v, l]) => (
          <button key={v} onClick={() => { setTab(v); setFiltre(""); }} data-testid={`tab-${v}`}
                  className={`h-11 rounded-md font-semibold text-sm ${tab === v ? "bg-[#12100D] text-[#D9A461]" : "text-stone-500"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {filtres.map(([v, l]) => (
          <button key={v} onClick={() => setFiltre(v)} data-testid={`filtre-${v || "tous"}`}
                  className={`h-11 px-4 rounded-lg text-sm font-semibold whitespace-nowrap border ${
                    filtre === v ? "bg-[#12100D] text-[#D9A461] border-[#D9A461]/40" : "bg-white text-stone-600 border-[#E6DFD1]"}`}>
            {l}
            {v === "a_envoyer" && nbAEnvoyer > 0 && (
              <span className="ml-1.5 bg-[#A32E20] text-white text-xs font-bold rounded-full px-2 py-0.5"
                    data-testid="badge-nb-a-envoyer">{nbAEnvoyer}</span>
            )}
          </button>
        ))}
      </div>

      <div className="panel !p-0 overflow-hidden">
        {rows.length === 0 ? (
          <p className="text-stone-500 text-sm py-10 text-center">
            {tab === "devis" ? t("liste.vide_devis") : t("liste.vide_factures")}
          </p>
        ) : (
          <div className="divide-y divide-[#E6DFD1]" data-testid={tab === "devis" ? "liste-devis" : "liste-factures"}>
            {rows.map((d) => (
              <button key={d.id}
                      onClick={() => navigate(tab === "devis" ? `/devis/${d.id}` : `/factures/${d.id}`)}
                      data-testid={`row-${tab}-${d.id}`}
                      className="w-full flex items-center justify-between gap-3 p-4 hover:bg-[#FDFBF7] text-left">
                <div className="min-w-0">
                  <div className="font-semibold">{d.numero}</div>
                  <div className="text-xs text-stone-500 truncate">{d.client_nom} · {dateFr(d.cree_le)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-serif font-semibold text-lg">{eur(d.total_ttc ?? d.montant_ttc)}</span>
                  <StatutBadge statut={d.statut} envoyeeLe={d.envoyee_le} testid={`badge-${d.id}`} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Modal open={corbeilleOpen} onClose={() => setCorbeilleOpen(false)} title={t("liste.corbeille_titre")} testid="modal-corbeille">
        <p className="text-xs text-stone-500 mb-4">{t("liste.corbeille_note")}</p>
        {corbeille.length === 0 ? (
          <p className="text-stone-500 text-sm py-6 text-center" data-testid="corbeille-vide">{t("liste.corbeille_vide")}</p>
        ) : (
          <div className="divide-y divide-[#E6DFD1]">
            {corbeille.map((d) => (
              <div key={d.id} className="flex items-center gap-3 py-3" data-testid={`corbeille-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{d.numero}</div>
                  <div className="text-xs text-stone-500">
                    {t(d.type === "devis" ? "liste.tab_devis" : "liste.tab_factures")} · {t("liste.supprime_le")} {dateFr(d.supprime_le)}
                  </div>
                </div>
                <span className="font-serif font-semibold text-sm shrink-0">{eur(d.total_ttc ?? d.montant_ttc)}</span>
                <button onClick={() => restaurer(d)} className="btn-secondary !h-10 !px-3 shrink-0"
                        data-testid={`btn-restaurer-${d.id}`}>
                  <RotateCcw size={15} /> {t("liste.restaurer")}
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
